import { InstantRegistrationFormContext } from "@iewccouk/Widgets/CreateAccount/InstantRegistrationForm";
import { CreateAccountFormContext } from "@iewccouk/Widgets/CreateAccount/RequestForm";
import siteMessage from "@insite/client-framework/SiteMessage";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import React from "react";
import { css } from "styled-components";

export const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
    items = "items",
    name = "name",
    heading = "heading",
    copy = "copy",
    labelOne = "labelOne",
    labelTwo = "labelTwo",
    typesOfRequest = "typesOfRequest",
}

export interface FieldItems {
    fields: {
        [fields.name]: string;
    };
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
        [fields.items]: FieldItems[];
        [fields.heading]: string;
        [fields.copy]: string;
        [fields.labelOne]: string;
        [fields.labelTwo]: string;
        [fields.typesOfRequest]: FieldItems[];
    };
}

interface RequestQuoteStyles {
    textFields?: TextFieldPresentationProps;
    selectFields?: SelectPresentationProps;
    checkboxGroupFields?: FieldSetGroupPresentationProps<CheckboxGroupComponentProps>;
    checkboxFields?: CheckboxPresentationProps;
    fieldLabel?: TypographyProps;
    required?: TypographyProps;
}

export const requestQuoteStyles: RequestQuoteStyles = {
    checkboxGroupFields: {
        labelProps: {
            variant: "body",
            forwardAs: "label",
            weight: "bold",
        },
    },
    checkboxFields: {
        typographyProps: {
            variant: "body",
            forwardAs: "label",
            color: "common.global.grayAlt700",
            css: css`
                max-width: calc(100% - 30px);
                min-width: max-content;
                width: 100%;
            `,
        },
        iconProps: {
            css: css`
                margin-top: 2px;
                align-self: start;
            `,
        },
    },
    fieldLabel: {
        variant: "body",
        as: "label",
        weight: "bold",
    },
    required: {
        forwardAs: "label",
        color: "#E82011",
        css: css`
            height: 30px;
        `,
    }
};

export const setRequestFormEffects = (
    isRequired: boolean,
    field: any,
    fieldName: string,
    validateField: () => boolean,
) => {
    const contactFormContext = React.useContext(CreateAccountFormContext);

    React.useEffect(() => {
        contactFormContext.validators[fieldName] = validateField;
        if (typeof field !== "undefined") {
            validateField();
        }
    }, [field]);

    React.useEffect(() => {
        contactFormContext.validators[fieldName] = validateField;
    }, [isRequired]);
};

export const setInstantFormEffects = (
    isRequired: boolean,
    field: any,
    fieldName: string,
    validateField: () => boolean,
) => {
    const instantRegistrationFormContext = React.useContext(InstantRegistrationFormContext);

    React.useEffect(() => {
        instantRegistrationFormContext.validators[fieldName] = validateField;
        if (typeof field !== "undefined") {
            validateField();
        }
    }, [field]);

    React.useEffect(() => {
        instantRegistrationFormContext.validators[fieldName] = validateField;
    }, [isRequired]);
};

export const validateFieldBy = (
    isRequired: boolean,
    label: string,
    fieldValue: any,
    setErrorMessage: (message: string) => void,
) => {
    const errorMessage = isRequired && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
    setErrorMessage(errorMessage);
    return !errorMessage;
};

export const getRfqFormLabelBuilt = (defaultValue: string): FieldDefinition => ({
    name: fields.label,
    displayName: "Label",
    editorTemplate: "TextField",
    fieldType: "Translatable",
    defaultValue,
    isRequired: true,
});

export const getRfqFormHintTextBuilt = (defaultValue: string): FieldDefinition => ({
    name: fields.hintText,
    displayName: "Hint Text",
    editorTemplate: "TextField",
    fieldType: "Translatable",
    defaultValue,
    isRequired: true,
});

export const getRfqFormCheckboxBuilt = (defaultValue = true): FieldDefinition => ({
    name: fields.isRequired,
    displayName: "Required Field",
    editorTemplate: "CheckboxField",
    defaultValue,
    fieldType: "General",
});

export const getRfqFormItemsBuilt = (displayName: string): FieldDefinition => ({
    name: fields.items,
    displayName,
    editorTemplate: "ListField",
    getDisplay: (item: HasFields) => {
        return item.fields.name;
    },
    defaultValue: [],
    fieldType: "Translatable",
    fieldDefinitions: [
        {
            name: fields.name,
            displayName: "Name",
            editorTemplate: "TextField",
            defaultValue: "",
            isRequired: true,
        },
    ],
});
